import { useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { supabase } from '../supabase/client';
import { useSelector, useDispatch } from 'react-redux';
import { setProfile, incrementOperation, decrementOperation, setOperationError } from '../Redux/store';

export default function LocationButton({ compact = false, iconOnly = false, onMessage }) {
  const dispatch = useDispatch();
  const userId = useSelector((s) => s.user.authUserId);
  const profile = useSelector((s) => s.user.profile);
  const [loading, setLoading] = useState(false);

  const onClick = () => {
    if (!('geolocation' in navigator)) {
      if (onMessage) onMessage({ message: 'Geolocation not supported by this browser.', type: 'error' });
      return;
    }
    if (!userId) {
      if (onMessage) onMessage({ message: 'You must be signed in.', type: 'error' });
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let city = '';
        let state = '';
        let country = '';
        let display = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        try {
          const res = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
          );
          if (res.ok) {
            const geo = await res.json();
            city = geo.city || geo.locality || '';
            state = geo.principalSubdivision || '';
            country = geo.countryCode?.toUpperCase() || geo.countryName || '';
            const built = [city, state, country].filter(Boolean).join(', ');
            if (built) display = built;
          }
        } catch (e) {
          /* Error handled */
        }

        // Increment global operation counter for Supabase update
        dispatch(incrementOperation('profile'));

        // Optimistic Redux update
        const oldLocation = profile.location;
        const oldLat = profile.lat;
        const oldLng = profile.lng;
        const oldCity = profile.city;
        const oldState = profile.state;
        const oldCountry = profile.country;
        dispatch(setProfile({ ...profile, lat, lng, city, state, country, location: display }));

        try {
          const { data, error } = await supabase
            .from('profiles')
            .update({ lat, lng, city, state, country, location: display })
            .eq('id', userId)
            .select()
            .maybeSingle();

          if (error) {
            // Set error message
            const errorMessage = error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('fetch')
              ? 'Error: No internet connection'
              : 'Error: Failed to save';
            dispatch(setOperationError({ type: 'profile', error: errorMessage }));
            // Revert on error
            dispatch(setProfile({ ...profile, location: oldLocation, lat: oldLat, lng: oldLng, city: oldCity, state: oldState, country: oldCountry }));
            if (onMessage) onMessage({ message: errorMessage, type: 'error' });
          } else if (data) {
            // Update with fresh data from Supabase
            dispatch(setProfile(data));
            if (onMessage) onMessage({ message: `Location saved: ${display}`, type: 'success' });
          }
        } catch (err) {
          // Set error message
          const errorMessage = err.message?.toLowerCase().includes('network') || err.message?.toLowerCase().includes('fetch')
            ? 'Error: No internet connection'
            : 'Error: Failed to save';
          dispatch(setOperationError({ type: 'profile', error: errorMessage }));
          // Revert on error
          dispatch(setProfile({ ...profile, location: oldLocation, lat: oldLat, lng: oldLng, city: oldCity, state: oldState, country: oldCountry }));
          if (onMessage) onMessage({ message: errorMessage, type: 'error' });
        } finally {
          // Decrement global operation counter
          dispatch(decrementOperation('profile'));
          setLoading(false);
        }
      },
      (geoErr) => {
        setLoading(false);
        if (onMessage) onMessage({ message: geoErr.message, type: 'error' });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (iconOnly || compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
        title="Use my current location"
        aria-label="Use my current location"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
      {loading ? 'Locating…' : 'Use my current location'}
    </button>
  );
}
